import _debounce from "lodash/debounce";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams } from "react-router";
import { useForm, FormProvider } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, Row, Form, Button, Container } from "react-bootstrap";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  CustomCard,
  ValiditySpinner,
  HorizontalDivider,
  ImageSizeRecommendationBadge
} from "../common-styling";
import Loader from "../loader/loader";
import {
  showError,
  showSuccess,
  showWarning
} from "../../services/toast-service";
import {
  updateKeyword,
  createKeyword,
  deleteKeyword,
  getKeywordById,
  getKeywordByName
} from "../../services/api-declaration";
import DeleteButton from "../delete-button";
import PreventReload from "../funnel-form/prevent-reload";
import GalleryTable from "../galllery-table/gallery-table";
import SpinnerButton from "../spinner-button/spinner-button";
import { KeywordFormSchema } from "../../services/validation-schema";
import ValidationMessage from "../validation-message/validation-message";
import { useTranslation } from "react-i18next";

const KeywordForm = ({
  fromModal = false,
  initialValue = null,
  createKeywordCallback = () => {}
}) => {
  const { t } = useTranslation();
  const params = useParams();
  const saveBtnRef = useRef();
  const keywordRef = useRef();
  const [isFormChanged, setIsFormChanged] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isNameValid, setIsNameValid] = useState(true);
  const [isNameChanged, setIsNameChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialValues = { name: initialValue };
  const methods = useForm({
    resolver: yupResolver(KeywordFormSchema),
    defaultValues: initialValues
  });
  const {
    formState: { errors, isSubmitSuccessful },
    reset,
    register,
    handleSubmit,
    getValues
  } = methods;

  const handleNameChange = (fieldVal) => {
    if (fieldVal) {
      saveBtnRef.current.disabled = true;
      setIsNameChanged(true);
    }
  };
  const handleDebounceCb = useMemo(
    () =>
      _debounce(async (name) => {
        try {
          if (name) {
            const response = await getKeywordByName({ name });
            if (response.data.data && response.data.data._id !== params.id) {
              setIsNameValid(false);
              saveBtnRef.current.disabled = true;
              showWarning(`Keyword with name ${name} is already taken`);
            } else {
              saveBtnRef.current.disabled = false;
              setIsNameValid(true);
            }
          }
        } catch (err) {
          console.log(err);
          showError("Error in getting keyword by name");
        } finally {
          setIsNameChanged(false);
        }
      }, 2000),
    // eslint-disable-next-line
    [setIsNameValid, saveBtnRef, params.id]
  );
  const updateInitialValues = async () => {
    try {
      setIsLoading(true);
      const response = await getKeywordById(params.id);
      reset(response.data.data);
      keywordRef.current = response.data.data;
    } catch (err) {
      console.log(err);
      showError("Error in getting keyword by id");
    } finally {
      setIsLoading(false);
    }
  };
  const navigateBack = () => navigate("/dashboard/keywords");
  const onSubmit = (formValues) => handleSubmitKeyword(formValues);
  const handleSubmitKeyword = async (formData) => {
    try {
      setIsSubmitting(true);
      if (params.id && !fromModal) {
        const response = await updateKeyword(params.id, formData);
        if (response) {
          showSuccess(response.data.message);
        }
      } else {
        const response = await createKeyword(formData);
        if (response) {
          showSuccess(response.data.message);
          if (fromModal) {
            createKeywordCallback(response.data);
          }
        }
      }
    } catch (error) {
      console.log(error);
      showError(error.response.data.message || "Error in saving keyword");
    } finally {
      setIsSubmitting(false);
      setIsFormChanged(false);
    }
  };

  useEffect(() => {
    if (isFormChanged) {
      const values = getValues();
      handleSubmitKeyword(values);
    }
    // eslint-disable-next-line
  }, [isFormChanged]);

  const keywordCb = useCallback(
    updateInitialValues,
    // eslint-disable-next-line
    [params.id, reset]
  );
  useEffect(() => {
    if (params.id) {
      keywordCb();
    }
  }, [keywordCb, params.id]);

  useEffect(() => {
    if (isSubmitSuccessful && !fromModal && !params.id) {
      navigateBack();
    }
    // eslint-disable-next-line
  }, [isSubmitSuccessful, fromModal, params.id]);

  return params.id && isLoading ? (
    <Loader />
  ) : (
    <FormProvider {...methods}>
      <PreventReload />
      <form
        onSubmit={(event) => {
          handleSubmit(onSubmit)(event);
        }}
      >
        {!fromModal && (
          <div
            className={`${
              fromModal ? "py-2" : "py-4"
            } px-0 d-flex align-items-center justify-content-between`}
          >
            <div className="d-flex align-items-center">
              <h5 className="mb-0 me-4">Keyword Form</h5>
            </div>
          </div>
        )}
        <CustomCard className="mb-4">
          <Container fluid className="p-4">
            <Row className="mb-4">
              <Col xs={12}>
                <Form.Group controlId="Keywords">
                  <Form.Label className={errors?.name && "text-danger"}>
                    {t("common.keyword")}
                    {errors?.name && "*"}
                  </Form.Label>
                </Form.Group>
                <Row className="align-items-center">
                  <Col xs={isNameChanged ? 11 : 12}>
                    <Form.Control
                      type="text"
                      placeholder={t("common.name")}
                      className={`form-control text-capitalize ${
                        errors?.name && "border-danger"
                      }`}
                      {...(register && {
                        ...register("name", {
                          onChange: ({ target: { value } }) => {
                            handleNameChange(value);
                            handleDebounceCb(value);
                            return value;
                          }
                        })
                      })}
                    />
                  </Col>
                  {isNameChanged && (
                    <Col xs={1} className="d-flex justify-content-center">
                      <ValiditySpinner
                        animation="border"
                        variant="secondary"
                        className="rounded-circle"
                      />
                    </Col>
                  )}
                </Row>
                {!isNameValid && !isNameChanged && (
                  <small className="mt-1 text-danger fw-bold">
                    <FontAwesomeIcon
                      className="me-2"
                      icon={faExclamationCircle}
                    />
                    {t("common.alreadyTaken")}
                  </small>
                )}
                {errors?.name && (
                  <ValidationMessage error={errors?.name.message} />
                )}
              </Col>
              <div>
                <ImageSizeRecommendationBadge
                  bg="warning"
                  className="text-dark mt-3"
                >
                  {t("common.keywordImageSizeRecommendation")}
                </ImageSizeRecommendationBadge>
              </div>
            </Row>
            <HorizontalDivider />
            <div className="mt-4 d-flex justify-content-between">
              {params.id && !fromModal && (
                <DeleteButton
                  variant={"box"}
                  deleteId={params.id}
                  onDelete={deleteKeyword}
                  onDeleteSuccess={navigateBack}
                  message={{
                    success: "deleteKeywordSuccess",
                    confirmation: "deleteKeywordMessage"
                  }}
                />
              )}
              <div className="d-flex justify-content-end">
                {!fromModal && (
                  <Button
                    className="me-2"
                    onClick={navigateBack}
                    variant="outline-secondary"
                  >
                    {t("common.cancel")}
                  </Button>
                )}
                {isSubmitting ? (
                  <SpinnerButton
                    btnText={`${t("common.save")}...`}
                    btnStyle="btn-success"
                  />
                ) : (
                  <Button
                    disabled={!isNameValid}
                    type="submit"
                    ref={saveBtnRef}
                    variant="outline-success"
                  >
                    {t("common.save")}
                  </Button>
                )}
              </div>
            </div>
          </Container>
        </CustomCard>
        {params.id && (
          <CustomCard className="mb-4">
            <Container fluid className="px-4">
              <GalleryTable
                hideAdvanceFilter={true}
                keyword={keywordRef.current}
              />
            </Container>
          </CustomCard>
        )}
      </form>
    </FormProvider>
  );
};
export default KeywordForm;
